/**
 * Script panel for displaying and playing back agent dialogue lines during a SIM.
 *
 * @module components/sim/takeSim/AgentScript
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect, useState} from "react";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PropTypes from "prop-types";
import {Grid, Tooltip} from '@material-ui/core';

import {useStyles} from "./AgentScript.js";
import AudioPlayButton from "../../common/AudioPlayButton";

/**
 * Creates a React component responsible for rendering agent-related curScripts with metadata
 * and additional helper functionalities such as highlighting keywords, audio playback support,
 * and displaying test results.
 *
 * The AgentScript component is designed to represent end-user defined curScripts (e.g., dialogue
 * or narrative content) accompanied by associated metadata like keywords, character details,
 * and test results. It provides interactivity, such as keyword highlighting and an audio
 * playback button, enhancing the usability of displayed data.
 *
 * @param {Object} props - The input properties passed to the AgentScript component.
 * @param {number} props.index - The index of the current curScript in a list, displayed when
 *                                indexing is enabled.
 * @param {Object} props.script - The curScript object containing various properties of the
 *     curScript including character details, metadata, and the curScript text.
 * @param {boolean} props.noIndex - A flag to suppress the rendering of the index column if true.
 *
 * @component
 *
 * @decurScription
 * - Highlights keywords within the curScript or transcribed text dynamically based on the
 *     `keywords` array in the curScript object.
 * - Includes support for rendering an audio playback button when `audio` or `audio_url` properties
 *   are defined in the curScript object.
 * - Displays test results, such as the count of passed and failed tests, with additional tooltips
 *   for more details.
 * - Dynamically renders character and curScript metadata such as name and type.
 * - Handles scenarios where certain data may be unavailable and adjusts the display accordingly.
 *
 * @returns {JSX.Element} A React component containing the curScript, metadata, and interactive
 *     features.
 */
const AgentScript = ({
    index,
    script,
    noIndex,
}) => {
    const classes = useStyles();
    const [characterType, setCharacterType] = useState("");
    const [characterName, setCharacterName] = useState("");
    const [curScript, setCurScript] = useState({});
    const [scriptId, setScriptId] = useState('');

    useEffect(
        () => {
            setCurScript(script);
        },
        [script]
    );

    useEffect(
        () => {
            if (curScript) {
                setCharacterType(
                    curScript.character?.masked_type?.length ?
                        curScript.character.masked_type :
                        curScript.character?.type ?? ''
                );
                setCharacterName(curScript.character?.name ?? '');
                setScriptId(curScript.id);
            }
        },
        [curScript]
    );

    /**
     * Highlights keywords within a given curScript string by wrapping them in styled HTML span
     * elements.
     *
     * This function processes a curScript string and a list of keywords from an object named
     * `curScript`.
     * It identifies each occurrence of the keywords within the curScript string, and highlights
     * those keywords by replacing them with a styled span element. The resulting string is then
     * returned.
     *
     * The `curScript` object is assumed to have the following properties:
     * - `curScript`: A string containing the curScript text to be processed.
     * - `keywords`: An array of strings representing the keywords to be highlighted in the
     * curScript text.
     *
     * When a keyword is found, it is highlighted by replacing it with a span element styled with a
     * background color, padding, and border-radius, and includes a tooltip ("Keyword").
     *
     * @returns {string} A string with keywords replaced by styled span elements for highlighting.
     */
    const highLightKeywords = () => {
        let str = curScript.script ?? '';
        const arr = curScript.keywords ?? [];
        const keywordArray = [];

        arr.map((ele) => {
            const substring = new RegExp(ele, 'gi');
            const res = str.match(substring);
            if (res && res.length) {
                keywordArray.push(res[0]);
            }
            return keywordArray;
        });
        keywordArray.map((ele) => {
            str = str.replaceAll(
                ele,
                `<span title='Keyword' style='background: #ADBBD4;padding: 4px;border-radius: 3px;'>${ele}</span>`,
            );
            return str;
        });
        return str;
    };

    /**
     * Highlights matching keywords within a given text with a styled span element.
     *
     * This function retrieves a string from `curScript.transcribed_text` or `curScript.script`,
     * and a list of keywords from `curScript.keywords`. It matches the keywords found in the
     * string and decorates the matching portions by wrapping them in a span element with specific
     * background and styling applied.
     *
     * @function highLightMatchKeywords
     * @returns {string} The updated string with matched keywords highlighted, or the original
     *     string if no matches are found.
     *
     * @decurScription
     * - Retrieves the input text from either `curScript.transcribed_text` or `curScript.script`.
     * - Extracts keywords to be matched from the `curScript.keywords` array.
     * - Searches for each keyword in a case-insensitive manner within the text.
     * - Replaces each occurrence of a keyword with a span element containing custom styles and
     *     tooltips.
     * - Returns the modified text with highlights or the original text if no keywords are matched.
     */
    const highLightMatchKeywords = () => {
        let str = curScript.transcribed_text ?? curScript.script;
        const arr = curScript.keywords ?? [];
        const keywordArray = [];

        arr.map((ele) => {
            const substring = new RegExp(ele, "gi");
            const res = str.match(substring);
            if (res && res.length) {
                keywordArray.push(res[0]);
            }
            return keywordArray;
        });
        keywordArray.map((ele) => {
            str = str.replaceAll(
                ele,
                `<span title='Matched keyword' style='background: #c4e89b;display: inline-block;padding: 0 .3rem;border-radius: 3px;'>${ele.toLowerCase()}</span>`
            );
            return str;
        });
        return str;
    };

    /**
     * Renders an audio play button for the given curScript.
     *
     * This function generates a React component that includes an `AudioPlayButton`
     * with properties derived from the provided `curScript` object. The availability
     * of the audio play button depends on the presence of `audio` or `audio_url`
     * in the `curScript` object. The function also displays the curScript ID alongside
     * the audio play button.
     *
     * @function
     * @returns {JSX.Element} A React fragment containing the `AudioPlayButton`
     *                         component and the curScript ID.
     */
    const renderAudioPlayButton = () => {
        return (
            <AudioPlayButton audio_id={scriptId}
                             classes={classes}
            />
        );
    }

    return (
        <Grid container key={scriptId} className={classes.agent}>
            {
                (scriptId && !noIndex) &&
                <Grid xs={1} item className={classes.index}>{index + 1}</Grid>
            }
            <Grid xs={11} item className={classes.scriptGrid}>
                <div className={classes.checkCircle}>
                    <CheckCircleIcon className={classes.checkCircleIcon}/>
                    {curScript?.myCount ? curScript.myCount : 1}{" "} take
                    <div>
                        {curScript?.tests_matched ? (
                            <Tooltip title={curScript?.tests_matched?.length ?
                                (curScript?.tests_matched.map((ele, i) => <span
                                    key={i}>{ele.name}</span>)) :
                                ""} arrow>
                                <p className={classes.curScript} style={{textAlign: "right"}}>
                                    {curScript?.tests_matched?.length ? (
                                        <span className={classes.bold}
                                              style={{color: "green"}}>{curScript.tests_matched.length} Passed Tests</span>
                                    ) : null}
                                </p>
                            </Tooltip>
                        ) : null}
                        {curScript?.missed_tests ? (
                            <Tooltip title={curScript?.missed_tests?.length ?
                                (curScript?.missed_tests.map((ele, i) => <span
                                    key={i}>{ele.name}</span>)) :
                                ""} arrow>
                                <p className={classes.curScript} style={{textAlign: "right"}}>
                                    {curScript?.missed_tests?.length ? (
                                        <span className={classes.bold}
                                              style={{color: "red"}}>{curScript.missed_tests.length} Failed Tests</span>
                                    ) : null}
                                </p>
                            </Tooltip>
                        ) : null}
                    </div>
                </div>

                {renderAudioPlayButton()}

                <div className={`${classes.scriptTypo} ${classes.scriptStyle}`}>
                    <span
                        className={`${classes.bold}`}>{characterName},
                    </span>
                    {' '}The{' '}{characterType}
                    &nbsp;
                    <span className={classes.curScript}>
                        {scriptId ? (
                            curScript.status === "Completed" ? (
                                <span
                                    dangerouslySetInnerHTML={{__html: highLightMatchKeywords()}}></span>
                            ) : (curScript?.audio_url ? (
                                    <span>{curScript.status}</span>
                                ) : (
                                    <>
                                        <Tooltip title={'Audio is NOT available'}>
                                            <span style={{
                                                color: '#333333',
                                                fontStyle: 'italic',
                                                fontSize: '1.25em',
                                                marginInline: '0.5em'
                                            }}>{curScript.script}</span>
                                        </Tooltip>
                                    </>
                                )
                            )
                        ) : curScript?.audio_url ? (
                            ""
                        ) : (
                            "( Not Generated! )"
                        )}
                    </span>
                    <br/>
                    <span className={classes.curScript}>
                        <span className={classes.bold}>SCRIPT</span>:{" "}
                        <span dangerouslySetInnerHTML={{
                            __html: highLightKeywords(curScript.script,
                                curScript.keywords
                            )
                        }}></span>
                        <br/>
                        <br/>
                        {scriptId && curScript?.has_tests ? (
                        <span>
                            <strong>Confidence: </strong>{Number(curScript?.grade_score).toFixed(2)}%
                        </span>
                        ) : ''}
                    </span>

                    {/*Correct Tests*/}
                    {curScript?.has_tests && curScript?.tests_matched ? (
                        <p className={classes.curScript} style={{textAlign: "right"}}>
                            {curScript?.tests_matched.length ? (
                                <span className={classes.bold}>Correct Tests: </span>
                            ) : null}
                            {curScript?.tests_matched?.length
                                ? curScript?.tests_matched?.map((ele, i) => (
                                    <span key={i}>
                                {i !== 0 ? (
                                    <span style={{color: "green"}}>, {ele.name}</span>
                                ) : (
                                    <span style={{color: "green"}}>{ele.name}</span>
                                )}
                            </span>
                                )) :
                                null
                            }
                        </p>
                    ) : null}

                    {/*Missed Tests*/}
                    {curScript?.has_tests && curScript?.missed_tests ? (
                        <p className={classes.curScript} style={{textAlign: "right"}}>
                            {curScript?.missed_tests?.length ? (
                                <span className={classes.bold}>Missed Tests : </span>
                            ) : null}
                            {curScript?.missed_tests?.length
                                ? curScript?.missed_tests?.map((ele, i) => {
                                    return (
                                        <span key={i}>
                                    {i !== 0 ? (
                                        <span style={{color: "red"}}>, {ele.name}</span>
                                    ) : (
                                        <span style={{color: "red"}}>{ele.name}</span>
                                    )}
                                </span>
                                    )
                                }) :
                                null
                            }
                        </p>
                    ) : null}

                    {/*Correct Keywords*/}
                    {curScript?.has_keywords && curScript?.keywords_matched ? (
                        <p className={classes.curScript} style={{textAlign: "right"}}>
                            {curScript?.keywords_matched?.length ? (
                                <span className={classes.bold}>Correct Keywords: </span>
                            ) : null}
                            {curScript?.keywords_matched?.length
                                ? curScript?.keywords_matched?.map((ele, i) => (
                                    <span key={i}>
                                        {i !== 0 ? (
                                            <span style={{color: "green"}}>, {ele}</span>
                                        ) : (
                                            <span style={{color: "green"}}>{ele}</span>
                                        )}
                                    </span>
                                ))
                                : null}
                        </p>
                    ) : null}

                    {/*Missed Keywords*/}
                    {curScript?.has_keywords && curScript?.missed_keywords ? (
                        <p className={classes.curScript} style={{textAlign: "right"}}>
                            {curScript?.missed_keywords?.length ? (
                                <span className={classes.bold}>Missed Keywords : </span>
                            ) : null}
                            {curScript?.missed_keywords?.length
                                ? curScript?.missed_keywords?.map((ele, i) => (
                                    <span key={i}>
                                        {i !== 0 ? (
                                            <span style={{color: "red"}}>, {ele}</span>
                                        ) : (
                                            <span style={{color: "red"}}>{ele}</span>
                                        )}
                                    </span>
                                ))
                                : null}
                        </p>
                    ) : null}
                </div>
                <div style={{clear: "both"}}></div>
            </Grid>
        </Grid>
    );
};

AgentScript.propTypes = {
    script: PropTypes.object,
    currentAgentScript: PropTypes.array,
    index: PropTypes.number,
    currentStatus: PropTypes.func,
    currentAudioPlaying: PropTypes.any,
    audioPlaying: PropTypes.string,
    totalLength: PropTypes.number,
    setLastFive: PropTypes.func,
    setTimePlayfun: PropTypes.func,
    pauseFunction: PropTypes.func,
    noIndex: PropTypes.bool,
};

export default AgentScript;
